import { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CategoriesPage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#34495e');
  const isAdmin = user?.role === 'admin';

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data.categories);
    } catch (err) { console.error('Failed to fetch categories:', err); }
  };

  const resetForm = () => { setName(''); setColor('#34495e'); setEditing(null); };
  const openCreate = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (cat) => { setEditing(cat); setName(cat.name); setColor(cat.color || '#34495e'); setDialogOpen(true); };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await api.put(`/categories/${editing.category_id}`, { name: name.trim(), color });
        toast.success('Category updated');
      } else {
        await api.post('/categories', { name: name.trim(), color });
        toast.success('Category created');
      }
      setDialogOpen(false); resetForm(); fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/categories/${catId}`);
      toast.success('Category deleted'); fetchCategories();
    } catch (err) { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#34495e]">Categories</h2>
          <p className="text-sm text-[#7F8C8D]">{categories.length} categories</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="create-category-btn">
            <Plus size={16} className="mr-1" /> Add Category
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
        <table className="w-full" data-testid="categories-table">
          <thead>
            <tr className="bg-[#F8F9FA]">
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Color</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Created</th>
              {isAdmin && <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-8 text-[#BDC3C7] text-sm">No categories yet</td></tr>
            ) : (
              categories.map(cat => (
                <tr key={cat.category_id} className="border-b border-[#E2E8F0] last:border-0">
                  <td className="px-4 py-3"><div className="w-6 h-6 rounded-sm" style={{ backgroundColor: cat.color }} /></td>
                  <td className="px-4 py-3 text-sm text-[#34495e]">{cat.name}</td>
                  <td className="px-4 py-3 text-sm text-[#7F8C8D]">{cat.created_at ? new Date(cat.created_at).toLocaleDateString() : '-'}</td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(cat)} className="p-1.5 text-[#7F8C8D] hover:text-[#34495e] transition-colors" data-testid={`edit-cat-${cat.category_id}`}><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(cat.category_id)} className="p-1.5 text-[#7F8C8D] hover:text-[#e74c3c] transition-colors" data-testid={`delete-cat-${cat.category_id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-sm bg-white border border-[#E2E8F0] rounded-sm" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight text-[#34495e]">{editing ? 'Edit Category' : 'New Category'}</DialogTitle>
            <DialogDescription className="sr-only">{editing ? 'Edit category details' : 'Create a new category'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Category name" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="category-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Color</Label>
              <div className="flex gap-2 mt-1 items-center">
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-10 h-10 rounded-sm border border-[#E2E8F0] cursor-pointer" data-testid="category-color-input" />
                <Input value={color} onChange={(e) => setColor(e.target.value)} className="flex-1 rounded-sm border-[#E2E8F0]" />
              </div>
            </div>
            <Button onClick={handleSubmit} className="w-full bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="category-submit-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
